import { z } from 'zod';

import { apiRequest } from '@/api/client';

import type { JsonObject } from './merge';

const contentSchema = z.record(z.string(), z.unknown());

const publicSiteContentSchema = z.object({
  sections: z.record(z.string(), contentSchema),
});

const sectionSchema = z.object({
  key: z.string(),
  content: contentSchema,
  updated_at: z.string().nullable().optional(),
  updated_by_user_id: z.string().nullable().optional(),
});

export type SiteContentSection = Omit<z.infer<typeof sectionSchema>, 'content'> & {
  content: JsonObject;
};

/** Every section the owner has edited, as visitors see it. No account needed. */
export async function fetchPublicSiteContent(): Promise<Record<string, JsonObject>> {
  const data = await apiRequest('/meta/site-content', publicSiteContentSchema, { auth: 'none' });
  return data.sections as Record<string, JsonObject>;
}

export async function fetchSiteContentSections(): Promise<SiteContentSection[]> {
  return (await apiRequest(
    '/platform/site-content',
    z.array(sectionSchema),
  )) as SiteContentSection[];
}

export async function saveSiteContentSection(
  key: string,
  content: JsonObject,
): Promise<SiteContentSection> {
  return (await apiRequest(`/platform/site-content/${encodeURIComponent(key)}`, sectionSchema, {
    method: 'PUT',
    body: { content },
  })) as SiteContentSection;
}

export async function resetSiteContentSection(key: string): Promise<void> {
  await apiRequest(`/platform/site-content/${encodeURIComponent(key)}`, z.null(), {
    method: 'DELETE',
  });
}

const assistResultSchema = z.object({
  summary: z.string(),
  content: contentSchema,
  changes: z.array(z.object({ path: z.string(), summary: z.string() })),
  refused: z.array(z.object({ path: z.string(), reason: z.string() })),
});

export type SiteContentAssistResult = Omit<z.infer<typeof assistResultSchema>, 'content'> & {
  content: JsonObject;
};

export interface SiteContentAssistTurn {
  role: 'owner' | 'assistant';
  text: string;
}

export interface SiteContentAssistRequest {
  message: string;
  /** The editor's draft, unsaved edits and all. */
  content: JsonObject;
  label?: string;
  description?: string;
  /** Item shapes for top-level lists, so an empty list can still grow. */
  templates?: JsonObject;
  history?: readonly SiteContentAssistTurn[];
}

/** Ask AI to edit the open section. Saves nothing; the answer goes into the form. */
export async function askSiteContentAssist(
  key: string,
  request: SiteContentAssistRequest,
): Promise<SiteContentAssistResult> {
  return (await apiRequest(
    `/platform/site-content/${encodeURIComponent(key)}/assist`,
    assistResultSchema,
    { method: 'POST', body: request },
  )) as SiteContentAssistResult;
}

export async function uploadSiteContentImage(file: File): Promise<string> {
  const body = new FormData();
  body.append('file', file);
  const data = await apiRequest('/platform/site-content/images', z.object({ url: z.string() }), {
    method: 'POST',
    body,
  });
  return data.url;
}
